import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useModal } from '../contexts/ModalContext'; // Uncommented

// Placeholder icons (replace with actual SVGs from Heroicons or similar)
const WrenchScrewdriverIcon = () => <span className="text-4xl">🛠️</span>;
const HeartIcon = () => <span className="text-4xl">❤️</span>;
const ShieldCheckIcon = () => <span className="text-4xl">🛡️</span>;
const AcademicCapIcon = () => <span className="text-4xl">🎓</span>;
const HomeModernIcon = () => <span className="text-4xl">🏠</span>; // Or MapPinIcon 📍

const AboutPage = () => {
  const { openModal } = useModal(); // Uncommented and initialized

  const coreValues = [
    {
      title: 'Hard Work & Dedication',
      icon: <WrenchScrewdriverIcon />,
      text: 'We believe in diligence and going the extra mile to ensure every HVAC job in Houston is done right, the first time.',
    },
    {
      title: 'Genuine Care for People',
      icon: <HeartIcon />,
      text: 'At our core, we love to help people. Your comfort, safety, and satisfaction are our top priorities.',
    },
    {
      title: 'Integrity & Honesty',
      icon: <ShieldCheckIcon />,
      text: 'Transparent pricing, honest assessments, and ethical service are the cornerstones of our business.',
    },
    {
      title: 'Expertise & Reliability',
      icon: <AcademicCapIcon />,
      text: 'Our certified technicians bring expertise and reliability to every service call, ensuring your HVAC system performs at its best.',
    },
    {
      title: 'Commitment to Houston',
      icon: <HomeModernIcon />,
      text: 'Proudly serving the Greater Houston area, we understand the unique climate challenges and are dedicated to our local community.',
    },
  ];

  const teamMembers = [
    {
      name: 'Pejman Ansari',
      title: 'Founder & Lead Technician',
      photo: '/images/pejman-ansari.jpg', // Placeholder
      bio: 'With over a decade of experience in the HVAC industry, Pejman founded Quality Air Houston in 2015 with a vision to provide honest, reliable, and high-quality HVAC services to the Houston community. He is passionate about solving complex HVAC issues and ensuring every customer receives top-notch care.',
    },
    // Add more team members here as placeholders or when available
    // { name: 'Jane Doe', title: 'Customer Service Manager', photo: '/images/team-member-2.jpg', bio: 'Jane ensures every customer interaction is smooth and pleasant.' },
    // { name: 'John Smith', title: 'Senior Technician', photo: '/images/team-member-3.jpg', bio: 'John brings 15 years of field experience to every job.' },
  ];

  return (
    <div className="bg-brand-background min-h-screen">
      {/* Hero/Header Section */}
      <header 
        className="relative py-16 md:py-24 text-center text-white bg-cover bg-center"
        style={{ backgroundImage: "url('/images/about-hero.jpg')" }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <div className="container mx-auto px-4 relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <br></br>
            <br></br>
            <br></br>
            Our Story: Serving Texas with Quality HVAC Care
          </h1>
          <p className="text-lg max-w-3xl mx-auto">
          Quality Air Houston values hard work, honesty, and a commitment to helping families and businesses in Texas feel their best. 
          </p>
        </div>
      </header>

      {/* Our Story / Founder's Message Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-8 md:gap-12 max-w-4xl mx-auto">
            <div className="flex-shrink-0">
              <img
                src="/images/pejman-ansari.jpg" // Placeholder
                alt="Pejman Ansari, Founder of Quality Air Houston"
                className="w-48 h-48 md:w-60 md:h-60 rounded-full object-cover shadow-lg border-4 border-white"
              />
            </div>
            <div className="text-center md:text-left">
              <h2 className="text-3xl font-semibold text-brand-gray-dark mb-3">
                A Message From Our Founder
              </h2>
              <p className="text-brand-gray mb-4 text-lg">
                "Driven by a passion for helping people and a commitment to hardworking, honest service, I founded Quality Air Houston in 2015. My goal was simple: to provide Houston residents and businesses with HVAC services they could truly trust. We're not just about fixing air conditioners or heaters; we're about ensuring your comfort, safety, and peace of mind. Thank you for considering Quality Air Houston."
              </p>
              <p className="font-semibold text-brand-gray-dark">Pejman Ansari</p>
              <p className="text-sm text-brand-gray">Founder, Quality Air Houston</p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Mission & Core Values Section */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-semibold text-brand-gray-dark text-center mb-10 md:mb-16">
            Our Commitment to You
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
            {coreValues.map((value) => (
              <div key={value.title} className="flex flex-col items-center text-center p-6 bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300">
                <div className="mb-4 text-brand-accent">{value.icon}</div>
                <h3 className="text-xl font-semibold text-brand-gray-dark mb-2">{value.title}</h3>
                <p className="text-brand-gray text-sm">{value.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* "Meet the Team" Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-semibold text-brand-gray-dark text-center mb-10 md:mb-16">
            Meet Your Houston HVAC Experts
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {teamMembers.map((member) => (
              <div key={member.name} className="bg-gray-50 p-6 rounded-lg shadow-lg text-center">
                <img
                  src={member.photo}
                  alt={member.name}
                  className="w-32 h-32 rounded-full mx-auto mb-4 object-cover border-4 border-white shadow-md"
                />
                <h3 className="text-xl font-semibold text-brand-gray-dark">{member.name}</h3>
                <p className="text-brand-accent mb-2">{member.title}</p>
                <p className="text-sm text-brand-gray">{member.bio}</p>
              </div>
            ))}
            {/* Placeholder for future team members if not enough are listed initially */}
            {teamMembers.length < 3 && (
              <div className="bg-gray-100 p-6 rounded-lg shadow-lg text-center flex flex-col items-center justify-center border-2 border-dashed border-gray-300">
                 <span className="text-6xl text-gray-400 mb-2">👨‍🔧</span>
                <h3 className="text-xl font-semibold text-gray-500">More Experts Coming Soon!</h3>
                <p className="text-sm text-gray-400">Our team is growing to serve you better.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Licenses & Certifications Section */}
      <section className="py-12 md:py-16 bg-brand-green-lightest"> {/* Or bg-gray-50 with different tint */}
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-semibold text-brand-gray-dark mb-6">
            Licensed & Certified for Your Peace of Mind
          </h2>
          <div className="max-w-3xl mx-auto bg-white p-6 rounded-lg shadow-md">
            <p className="text-lg font-semibold text-brand-gray-dark mb-1">
              Texas Air Conditioning & Refrigeration Contractor License:
            </p>
            <p className="text-xl font-bold text-brand-accent mb-3">
              TACLB113848E
            </p>
            <p className="text-xs text-brand-gray leading-relaxed">
              Regulated by the Texas Department of Licensing and Regulation, P. O. Box 12157, Austin, Texas 78711, 1-800-803-9202, 512-463-6599, <a href="https://www.tdlr.texas.gov" target="_blank" rel="noopener noreferrer" className="text-brand-green hover:underline">www.tdlr.texas.gov</a>.
            </p>
          </div>
        </div>
      </section>

      {/* "Why Choose Quality Air Houston?" Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-semibold text-brand-gray-dark text-center mb-10">
            Why Choose Quality Air Houston?
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-gray-50 p-6 rounded-lg shadow">
              <h3 className="text-xl font-semibold text-brand-accent mb-2">Locally Owned & Operated</h3>
              <p className="text-brand-gray text-sm">Proudly serving Houston since 2015, we're your neighbors, dedicated to our community.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg shadow">
              <h3 className="text-xl font-semibold text-brand-accent mb-2">Customer Satisfaction Focus</h3>
              <p className="text-brand-gray text-sm">We prioritize your satisfaction, aiming to build long-term relationships through exceptional service.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg shadow">
              <h3 className="text-xl font-semibold text-brand-accent mb-2">Quality Parts & Equipment</h3>
              <p className="text-brand-gray text-sm">We use high-quality parts and equipment to ensure lasting repairs and installations.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Community Involvement (Optional Section) */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-semibold text-brand-gray-dark mb-6">
            Giving Back to Our Houston Community
          </h2>
          <p className="text-brand-gray max-w-2xl mx-auto mb-6">
            Quality Air Houston is committed to supporting the community that trusts us with their comfort. We believe in giving back and actively look for ways to support local initiatives.
          </p>
          {/* Example: <img src="/images/community-event.jpg" alt="Community Involvement" className="mx-auto rounded-lg shadow-md max-w-md" /> */}
        </div>
      </section>

      {/* Call to Action (CTA) Section */}
      <section className="py-16 md:py-20 bg-brand-green-light">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-brand-gray-dark mb-4">
            Experience the Quality Air Houston Difference
          </h2>
          <p className="text-lg text-brand-gray mb-8 max-w-2xl mx-auto">
            Ready to work with an HVAC company that genuinely cares? Contact us today for your heating, cooling, and air quality needs in Houston.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
            <button
              onClick={() => openModal('scheduleService')} // Updated onClick handler
              className="bg-brand-accent hover:bg-brand-accent-dark text-white font-semibold py-3 px-8 rounded-lg shadow-lg transition duration-300 ease-in-out text-lg"
            >
              Schedule Service
            </button>
            <RouterLink
              to="/contact"
              className="bg-white hover:bg-gray-100 text-brand-accent font-semibold py-3 px-8 rounded-lg shadow-lg transition duration-300 ease-in-out text-lg border border-brand-accent"
            >
              Ask a Question
            </RouterLink>
            <RouterLink
              to="/services"
              className="text-brand-green hover:text-brand-green-dark font-semibold transition duration-300 ease-in-out"
            >
              View Our Services
            </RouterLink>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage; 